import { Injectable } from '@angular/core';
import gql from 'graphql-tag';
import { delay, first, map, mergeMapTo, switchMap, tap } from 'rxjs/operators';
import { combineLatest, from, of, race, throwError } from 'rxjs';
import { UiPromiseService } from 'ngx-zcs-components';

import { GraphqlApiService } from '../graphql-api/graphql-api.service';
import { OnCommandResponseSubscription, SendCommandAdvancedMutation, SendCommandBasicInPlantStatusMutation, SendCommandBasicMutation, UpdateConfigInConnextMutation } from '../telemetry/index.graph-ql';
import { isNotNullOrUndefined } from '../utils';

const commandOpts = {
  timeout: 80000,
  errorMessage: 'Timeout exceeded',
  loading: true,
  loadingMessage: 'Sending command',
};

const responseQuery = gql`
  fragment cmdres on CMDRes {
    timestamp
    action {
      msg
      success
      msgCode
    }
  }`;

const cmdQuery = gql`
  fragment cmdreq on CMDReq {
    deviceId
  }`;

@Injectable({
  providedIn: 'root',
})
export class CmdConnextService {
  readonly client$ = this.graphqlApiService.client$.pipe(
    isNotNullOrUndefined(),
  );

  constructor(
    private readonly graphqlApiService: GraphqlApiService,
    private readonly onCommandResponseSubscription: OnCommandResponseSubscription,
    private readonly sendCommandRequestMutation: SendCommandAdvancedMutation,
    private readonly sendCommandBasicMutation: SendCommandBasicMutation,
    private readonly sendCommandBasicInPlantStatusMutation: SendCommandBasicInPlantStatusMutation,
    private readonly updateConfigInConnextMutation: UpdateConfigInConnextMutation,
    private readonly uiPromiseService: UiPromiseService,
  ) {
    this.sendCommand = this.sendCommand.bind(this);
  }

  /** @abstract This method need to response success.success = true to be executed successfully */
  async sendCommand(deviceId: string, cmd: string, noAutoOwner = true, advanced = false, opts = commandOpts) {
    const service = advanced ? this.sendCommandRequestMutation : this.sendCommandBasicMutation;
    return this.sendCommandWithService(service, deviceId, cmd, noAutoOwner, opts);
  }

  async sendCommandWithService(
      service: SendCommandAdvancedMutation | SendCommandBasicMutation,
      deviceId: string,
      cmd: string,
      noAutoOwner = true,
      opts = commandOpts,
    ) {
    deviceId = deviceId.toLowerCase();
    const transactionId = `${new Date().getTime()}`;
    const responseSubscription$ = from(await this.onCommandResponseSubscription.execute(responseQuery, {
      deviceId,
      transactionId,
    }).pipe(first()).toPromise() as any).pipe(first());

    const cmdRoot = cmd.split(' ')[0].trim();
    const cmdArgs = cmd.replace(cmdRoot, '').trim();

    const cmd$ = of(null).pipe(
      switchMap(() => service.execute(cmdQuery, {
        deviceId, action: { args: [cmdArgs], cmd: cmdRoot }, transactionId, noAutoOwner,
      })),
    );

    const Error$ = of(null).pipe(
      delay(opts.timeout),
      mergeMapTo(throwError(opts.errorMessage)),
    );

    return await this.uiPromiseService.run(
      async () => await race(Error$, combineLatest([cmd$, responseSubscription$])).pipe(
        map((resp) => (resp.pop() as any)?.data?.onCommandResponse?.action),
        tap((resp) => console.warn(resp)),
      ).toPromise(),
      {
        loading: opts.loading,
        loadingMessage: opts.loadingMessage,
      },
    );
  }

  async sendCommandStrategy(connextId: string, cmd: string, loadingMessage: string, inAdvancedPlant: boolean, checkConfig: boolean, noAutoOwner?: boolean, advancedPlantId?: string): Promise<{ error?: any; success?: any; }>{
    let response;
    if ( inAdvancedPlant && advancedPlantId ) {
      response = await this.sendCommandInPlantStatusWithService(advancedPlantId, connextId, cmd);
    } else {
      response = await this.sendCommand(connextId, cmd, noAutoOwner, false);

      if( !response?.success?.success ) {
        return { error: true };
      }
    }

    if( checkConfig ) {
      await this.updateConnextConfigVerified(connextId, loadingMessage);
    }
    return response;
  }

  async sendCommandInPlantStatusWithService(
    plantId: string,
    connextId: string,
    cmd: string,
    opts = commandOpts,
  ) {
    const deviceId = connextId.toLowerCase();
    const transactionId = `${new Date().getTime()}`;
    const responseSubscription$ = from(await this.onCommandResponseSubscription.execute(responseQuery, {
      deviceId,
      transactionId,
    }).pipe(first()).toPromise() as any).pipe(first());

    const cmdRoot = cmd.split(' ')[0].trim();
    const cmdArgs = cmd.replace(cmdRoot, '').trim();

    const cmd$ = of(null).pipe(
      switchMap(() => this.sendCommandBasicInPlantStatusMutation.execute(cmdQuery, {
        plantId, deviceId, action: { args: [cmdArgs], cmd: cmdRoot }, transactionId,
      })),
    );

    const Error$ = of(null).pipe(
      delay(opts.timeout),
      mergeMapTo(throwError(opts.errorMessage)),
    );

    return await this.uiPromiseService.run(
      async () => await race(Error$, combineLatest([cmd$, responseSubscription$])).pipe(
        map((resp) => (resp.pop() as any)?.data?.onCommandResponse?.action),
        tap((resp) => console.warn(resp)),
      ).toPromise(),
      {
        loading: opts.loading,
        loadingMessage: opts.loadingMessage,
      },
    );
  }

  async updateConnextConfigVerified(connextId: string, loadingMessage: string) {
    return await this.uiPromiseService.run(async () => {
      const query = gql`
      fragment connextconfig on ConnextConfig {
        configVerified
      }`;

      return await this.updateConfigInConnextMutation.execute(query, {
        deviceId: connextId,
        configVerified: true,
        remove: false,
      });
    }, {
      loading: true,
      loadingMessage,
    });
  }
}
